import { lazy, Suspense, useEffect } from "react";
import DeskCloseX from "@/components/common/DeskCloseX";
import { createPortal } from "react-dom";
import { Loader2, ExternalLink } from "lucide-react";
import { useNavigate } from "@/hooks/useDeskNavigate";
import AviDetailModal from "@/components/avi/AviDetailModal";

const DocumentDetail = lazy(() => import("@/pages/DocumentDetail"));
const TaskDetail = lazy(() => import("@/pages/TaskDetail"));

/**
 * QuickViewDialog — spec iter307 : aperçu rapide d'un document / d'une tâche
 * dans une modale, sans quitter la page. Réutilise les pages détail
 * existantes montées avec `idOverride` (hors route).
 */
export default function QuickViewDialog({ kind, id, onClose }) {
  const navigate = useNavigate();
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (kind === "avi") return <AviDetailModal aviId={id} onClose={onClose} />;

  const path = kind === "task" ? `/taches/${id}` : `/documents/${id}`;
  const Page = kind === "task" ? TaskDetail : DocumentDetail;
  return createPortal(
    <div className="fixed inset-0 flex items-stretch justify-center" style={{ zIndex: 10000, background: "rgba(0,0,0,0.72)", padding: "24px 24px 24px 114px" }}
      onClick={onClose} data-testid="quick-view-dialog">
      <div className="relative flex-1 min-w-0 rounded-xl overflow-hidden flex flex-col"
        style={{ background: "#1B1B1B", border: "1px solid #2A2A2A", boxShadow: "0 24px 60px rgba(0,0,0,0.6)" }}
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-2 shrink-0" style={{ borderBottom: "1px solid #2A2A2A", background: "#161616" }}>
          <span className="text-[11px] uppercase tracking-widest text-muted-foreground">Aperçu rapide</span>
          <span className="flex items-center gap-2">
            <button type="button" onClick={() => { onClose(); navigate(path); }}
              className="inline-flex items-center gap-1.5 text-[11.5px] px-2 py-1 rounded-md hover:bg-white/10 transition-colors"
              style={{ color: "#7bb3e8" }} data-testid="quick-view-open-page"><ExternalLink size={13} /> Ouvrir la page</button>
            <DeskCloseX onClick={onClose} testId="quick-view-close" />
          </span>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto">
          <Suspense fallback={<div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-muted-foreground" /></div>}>
            <Page idOverride={id} />
          </Suspense>
        </div>
      </div>
    </div>,
    document.body,
  );
}
